/**
 * The failure list of a recursive access-control (`acl`) job (S8 Task 8.11), rendered from the job's COMMITTED manifest
 * (`job.acl.failures`), never from the 100-capped `job.errors`: each listed, unresolved failure by its PATH (the Retry
 * button sends the ledger record key — `acl:<epoch>:failure:<seq>` — which is never displayed, finding S8-R2-03), the
 * reason, and what can be done — Retry (single path, conditional), no Retry when the service semantics cannot be
 * reproduced by one write (`AclRetryUnavailable` / `DefaultAclOnFile`, D16: Restart only), or "Propagate again from this
 * directory" for a failed directory whose subtree was not visited (row R3). Fixed failures are hidden; the unlisted
 * remainder is one line with no Retry (E3, S8-R2-04). Retry is offered only where main accepts it — a `partial` or
 * `failed` job (review opus F9); a cancelled or interrupted job offers Resume / Restart on its row instead.
 */
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { ReasonButton } from '../../../components/ui/ReasonButton';
import { aclRecordKey } from '../../../core/engines/azureStorageAclJobKeys';
import { aclUnlistedFailuresSentence as aclOmittedLine } from '../../../core/engines/azureStorageAclJobText';
import type { HnsAclFailure, TransferJob } from '../../../types/azureStorage';
import { trackCopyReport, trackErrorToggle } from '../hooks/useTransferActions';

type Props = {
  job: TransferJob;
  onRetry: (jobId: string, items: string[]) => void;
  onPropagateAgain: (job: TransferJob, failure: HnsAclFailure) => void;
  /** Review C1-05: the row's recovery refusal (connection removed, read-only) — Retry and Propagate again say it. */
  recoveryRefusal?: string;
};

const NO_RETRY_CODES: ReadonlySet<string> = new Set(['AclRetryUnavailable', 'DefaultAclOnFile']);
const plural = (n: number): string => `${n} failed path${n === 1 ? '' : 's'}`;

function action(job: TransferJob, failure: HnsAclFailure, props: Props) {
  if (failure.code === 'AclSubtreeNotVisited' && failure.isDirectory) {
    return <ReasonButton variant="outline" size="xs" side="right" className="w-fit" reason={props.recoveryRefusal} onClick={() => { if (!props.recoveryRefusal) props.onPropagateAgain(job, failure); }} data-testid={`transfer-acl-propagate-again-${job.id}-${failure.seq}`}>Propagate again from this directory</ReasonButton>;
  }
  if (failure.code && NO_RETRY_CODES.has(failure.code)) return null;
  if (failure.retry?.state === 'selected' || failure.retry?.state === 'dispatched') return <span className="text-muted-foreground">Retrying…</span>;
  if (job.state !== 'partial' && job.state !== 'failed') return null;
  const key = aclRecordKey({ role: 'failure', epoch: job.acl!.epoch, seq: failure.seq });
  return <ReasonButton variant="outline" size="xs" side="right" className="w-fit" reason={props.recoveryRefusal} onClick={() => { if (!props.recoveryRefusal) props.onRetry(job.id, [key]); }} data-testid={`transfer-acl-retry-${job.id}-${failure.seq}`}>Retry</ReasonButton>;
}

export function AclTransferFailures(props: Props) {
  const { job } = props;
  const [open, setOpen] = useState(false);
  const acl = job.acl;
  if (!acl) return null;
  const listed = acl.failures.filter((failure) => failure.retry?.state !== 'succeeded');
  if (listed.length === 0 && acl.failuresOmitted === 0) return null;
  const total = listed.length + acl.failuresOmitted;

  const copyReport = () => {
    const lines = listed.map((failure) => `${failure.path}\t${failure.category}\t${failure.message}`);
    if (acl.failuresOmitted > 0) lines.push(aclOmittedLine(acl.failuresOmitted));
    trackCopyReport(job);
    void navigator.clipboard?.writeText(lines.join('\n'));
  };
  const toggle = () => {
    trackErrorToggle(job, !open);
    setOpen(!open);
  };

  return (
    <div className="mt-1">
      <Button variant="ghost" size="xs" onClick={toggle} data-testid={`transfer-errors-toggle-${job.id}`}>{open ? 'Hide failed paths' : `Show ${plural(total)}`}</Button>
      {open && (
        <div className="mt-1 rounded-md border border-border bg-muted/30 p-2 text-xs">
          <ul className="space-y-2">
            {listed.map((failure) => (
              <li key={failure.seq} className="grid gap-1" data-testid={`transfer-acl-failure-${job.id}-${failure.seq}`}>
                <span className="font-medium break-words [overflow-wrap:anywhere]">{failure.path}{failure.isDirectory ? '/' : ''}</span>
                <span className="text-muted-foreground break-words">{failure.message}</span>
                {action(job, failure, props)}
              </li>
            ))}
          </ul>
          {acl.failuresOmitted > 0 && <p className="mt-2 text-muted-foreground" data-testid={`transfer-acl-omitted-${job.id}`}>{aclOmittedLine(acl.failuresOmitted)}</p>}
          <Button variant="ghost" size="xs" className="mt-2" onClick={copyReport} data-testid={`transfer-copy-report-${job.id}`}>Copy error report</Button>
        </div>
      )}
    </div>
  );
}
